import {
  type FC, useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { type ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import { useGeolocationInputProps } from '~/_shared/components/textInputWithGeolocation/useGeolocationInputProps';
import { type UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import {
  GroupRadiusEditLevel, ProximityType,
} from '~/_shared/types/proximity/proximity.enums';
import {
  isDriveTimePolygonSettings, isIndividualRadius, type Proximity, type ProximityModel,
} from '~/_shared/types/proximity/proximity.types';
import { type SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { type GeoLocation } from '~/_shared/utils/geolocation/geolocation';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { useIsDriveTimePolygonToolEnabled } from '~/proximity/panel/useIsDriveTimePolygonToolEnabled';
import { SelectLocationConfiguration } from '~/store/frontendState/mapTools/selectLocation/selectLocation.state';
import {
  setApplyTo, setBorderColor, setBorderLineWidth, setProximityColor, setProximityWithin, setSelectedGroup,
  setSelectedGroupColumn, setStartingLocation, setTravelTimeHours, setTravelTimeMinutes, setType, setUnit,
} from '~/store/frontendState/proximityTool/proximityTool.actionCreators';
import { useProximityToolSelector } from '~/store/frontendState/proximityTool/proximityTool.selectors';
import {
  addProximity, setHideLabels, setHideSmallRadii,
} from '~/store/mapSettings/proximity/mapSettingsProximity.actionCreators';
import { useSpreadsheetColumns } from '~/store/matchupData/matchupDataSelectors.hook';
import { type GroupData } from '~/store/spreadsheetData/spreadsheetData.state';
import { type ProximityRequestCreateObject } from '../../proximity.factory';
import { useZoomToProximity } from '../../useZoomToProximity';
import { ProximityPanelFormComponent } from './proximityPanelForm.component';
import { createProximityRequestFromPropsWithDefaults } from './proximityPanelForm.helpers';

export const DEFAULT_PROXIMITY_BG_OPACITY = 0.35;
export const DEFAULT_PROXIMITY_BORDER_OPACITY = 1;

type ProximityPanelContainerProps = {
  className?: string;
  showLabelsHiddenWarningDueToPerformance: boolean;
  showLabelsHiddenWarningDueToTooSmall: boolean;
  proximityList: ReadonlyArray<Proximity>;
  proximityType: ProximityType;

  scrollToProximityInList: (proximityId: string) => void;
};

export const ProximityPanelFormContainer: FC<ProximityPanelContainerProps> = (props) => {
  const { proximityList, scrollToProximityInList, proximityType } = props;

  const hideLabels = useSelector(state => state.map.mapSettings.data.proximity.hideLabels);
  const hideSmallRadii = useSelector(state => state.map.mapSettings.data.proximity.hideSmallRadii);
  const isDriveTimePolygonToolEnabled = useIsDriveTimePolygonToolEnabled();
  const columns = useSpreadsheetColumns();
  const { zoomToRadius } = useZoomToProximity();
  const dispatch = useDispatch();

  const { applyTo, unit: proximityToolUnit, travelTimeHours, travelTimeMinutes, proximityWithin, selectedGroup,
    selectedGroupColumn, proximityColor, borderColor, borderLineWidth, startingLocation } = useProximityToolSelector();

  const allowedProximityTypes: ReadonlyArray<ProximityType> = useMemo(() => {
    const types = [ProximityType.DistanceRadius];

    if (isDriveTimePolygonToolEnabled) {
      types.push(ProximityType.DriveTimePolygon);
    }

    return types;
  }, [isDriveTimePolygonToolEnabled]);

  const setStartingLocationState = useCallback((startingLocation: GeoLocation) => {
    dispatch(setStartingLocation(startingLocation));
  }, [dispatch]);

  const {
    address,
    wasAddressTyped,
    geolocationInputProps,
    getLatLngFromAddress,
  } = useGeolocationInputProps({
    configuration: SelectLocationConfiguration.SetProximityToolStartLocation,
    startingLocationState: startingLocation,
    setStartingLocationState,
  });

  const onProximityAdd = useCallback((proximity: ProximityModel) => {
    if (isDriveTimePolygonSettings(proximity)) {
      dispatch(addProximity(proximity, true));
    }
    else {
      dispatch(addProximity(proximity));
      if (isIndividualRadius(proximity)) {
        zoomToRadius(proximity);
      }
    }
    scrollToProximityInList(proximity.id);
  }, [dispatch, zoomToRadius, scrollToProximityInList]);

  const addProximityRadius = useCallback(async () => {
    const innerSelectedGroupColumn = columns.find(column => (
      column.id === selectedGroupColumn?.columnId
      && column.spreadsheetId === selectedGroupColumn?.spreadsheetId
    ));

    const proximityRequest: ProximityRequestCreateObject = {
      type: proximityType,
      applyTo,
      unit: proximityToolUnit,
      startingLocation,
      distance: proximityWithin,
      borderColor: borderColor.hex,
      borderWidth: borderLineWidth,
      borderOpacity: DEFAULT_PROXIMITY_BORDER_OPACITY,
      selectedGroupColumn: innerSelectedGroupColumn,
      fillColor: proximityColor.hex,
      fillOpacity: DEFAULT_PROXIMITY_BG_OPACITY,
      selectedGroup,
      travelTimeMinutes,
      travelTimeHours,
    };

    if (wasAddressTyped && applyTo !== GroupRadiusEditLevel.Group) {
      const response = await getLatLngFromAddress({ address });
      proximityRequest.startingLocation = response;
      const newProximity = createProximityRequestFromPropsWithDefaults(proximityRequest);

      onProximityAdd(newProximity);
    }
    else {
      const newProximity = createProximityRequestFromPropsWithDefaults(proximityRequest);
      onProximityAdd(newProximity);
    }
  }, [address, applyTo, columns, onProximityAdd, proximityColor.hex, proximityToolUnit,
    proximityWithin, selectedGroup, selectedGroupColumn?.columnId, selectedGroupColumn?.spreadsheetId, startingLocation,
    travelTimeHours, travelTimeMinutes, proximityType, wasAddressTyped, borderColor, borderLineWidth, getLatLngFromAddress,
  ]);

  const onSetHideLabels = useCallback((hideLabels: boolean) =>
    dispatch(setHideLabels(hideLabels)), [dispatch]);

  const onSetHideSmallRadii = useCallback((hideSmallRadii: boolean) =>
    dispatch(setHideSmallRadii(hideSmallRadii)), [dispatch]);

  const onSetType = useCallback((type: ProximityType) => {
    dispatch(setType(type));
  }, [dispatch]);

  const onSetApplyTo = useCallback((applyTo: GroupRadiusEditLevel) =>
    dispatch(setApplyTo(applyTo)), [dispatch]);

  const onSetUnit = useCallback((unit: UnitSystem) =>
    dispatch(setUnit(unit)), [dispatch]);

  const onSetTravelTimeHours = useCallback((travelTimeHours: number | null) =>
    dispatch(setTravelTimeHours(travelTimeHours)), [dispatch]);
  const onSetTravelTimeMinutes = useCallback((travelTimeMinutes: number | null) =>
    dispatch(setTravelTimeMinutes(travelTimeMinutes)), [dispatch]);

  const onSetProximityWithin = useCallback((proximityWithin: number | null) =>
    dispatch(setProximityWithin(proximityWithin)), [dispatch]);

  const onSetSelectedGroup = useCallback((selectedGroup: GroupData | null) =>
    dispatch(setSelectedGroup(selectedGroup)), [dispatch]);

  const onSetSelectedGroupColumn = useCallback((selectedGroupColumnId: SpreadsheetColumnId) =>
    dispatch(setSelectedGroupColumn(selectedGroupColumnId)), [dispatch]);

  const onSetProximityColor = useCallback((proximityColor: ColorResult) =>
    dispatch(setProximityColor(proximityColor)), [dispatch]);

  const onSetBorderColor = useCallback((borderColor: ColorResult) =>
    dispatch(setBorderColor(borderColor)), [dispatch]);

  const onSetBorderLineWidth = useCallback((borderLineWidth: number) =>
    dispatch(setBorderLineWidth(borderLineWidth)), [dispatch]);

  return (
    <ProximityPanelFormComponent
      {...props}
      allowedProximityTypes={allowedProximityTypes}
      addProximityRadius={addProximityRadius}
      applyTo={applyTo}
      hideLabels={hideLabels}
      hideSmallRadii={hideSmallRadii}
      onSetApplyTo={onSetApplyTo}
      onSetHideLabels={onSetHideLabels}
      onSetHideSmallRadii={onSetHideSmallRadii}
      onSetProximityColor={onSetProximityColor}
      onSetBorderColor={onSetBorderColor}
      onSetBorderLineWidth={onSetBorderLineWidth}
      onSetProximityWithin={onSetProximityWithin}
      onSetSelectedGroup={onSetSelectedGroup}
      onSetSelectedGroupColumn={onSetSelectedGroupColumn}
      onSetTravelTimeHours={onSetTravelTimeHours}
      onSetTravelTimeMinutes={onSetTravelTimeMinutes}
      onSetType={onSetType}
      onSetUnit={onSetUnit}
      proximityColor={proximityColor}
      proximityGeolocationInputProps={geolocationInputProps}
      borderColor={borderColor}
      borderLineWidth={borderLineWidth}
      proximityList={proximityList}
      proximityWithin={proximityWithin}
      selectedGroup={selectedGroup}
      selectedGroupColumnId={selectedGroupColumn}
      spreadsheetColumns={columns}
      travelTimeHours={travelTimeHours}
      travelTimeMinutes={travelTimeMinutes}
      type={proximityType}
      unit={proximityToolUnit}
    />
  );
};
